import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalculator, FiDollarSign, FiClock, FiInfo, FiToggleLeft, FiToggleRight } = FiIcons;

function App() {
  const [mode, setMode] = useState('hourly'); // 'hourly' or 'annual'
  const [hourlyWage, setHourlyWage] = useState('');
  const [annualSalary, setAnnualSalary] = useState('');
  const [results, setResults] = useState(null);

  // 2025 Federal Tax Brackets (Single Filer)
  const federalBrackets = [
    { min: 0, max: 10275, rate: 0.10 },
    { min: 10275, max: 41775, rate: 0.12 },
    { min: 41775, max: 89450, rate: 0.22 },
    { min: 89450, max: 190750, rate: 0.24 },
    { min: 190750, max: 364200, rate: 0.32 },
    { min: 364200, max: 462500, rate: 0.35 },
    { min: 462500, max: Infinity, rate: 0.37 }
  ];

  // Maryland State Tax Brackets - UPDATED for 2025
  const mdBrackets = [
    { min: 0, max: 1000, rate: 0.02 },
    { min: 1000, max: 2000, rate: 0.03 },
    { min: 2000, max: 3000, rate: 0.04 },
    { min: 3000, max: 100000, rate: 0.0475 },
    { min: 100000, max: 125000, rate: 0.05 },
    { min: 125000, max: 150000, rate: 0.0525 },
    { min: 150000, max: 250000, rate: 0.055 },
    { min: 250000, max: 500000, rate: 0.0575 },
    { min: 500000, max: 1000000, rate: 0.0625 },
    { min: 1000000, max: Infinity, rate: 0.065 }
  ];

  const calculateTax = (income, brackets) => {
    let tax = 0;
    for (const bracket of brackets) {
      if (income > bracket.min) {
        const taxableInBracket = Math.min(income, bracket.max) - bracket.min;
        tax += taxableInBracket * bracket.rate;
      }
    }
    return tax;
  };

  const calculateTakeHome = (grossAnnual) => {
    const standardDeduction = 15000; // 2025 standard deduction
    const taxableIncome = Math.max(0, grossAnnual - standardDeduction);
    
    const federalTax = calculateTax(taxableIncome, federalBrackets);
    const stateTax = calculateTax(taxableIncome, mdBrackets);
    const socialSecurity = grossAnnual * 0.062;
    const medicare = grossAnnual * 0.0145;
    
    const totalTax = federalTax + stateTax + socialSecurity + medicare;
    const netAnnual = grossAnnual - totalTax;
    
    return {
      grossAnnual,
      federalTax,
      stateTax,
      socialSecurity,
      medicare,
      totalTax,
      netAnnual,
      netBiweekly: netAnnual / 26,
      hourlyEquivalent: grossAnnual / (40 * 52)
    };
  };

  useEffect(() => {
    if (mode === 'hourly' && hourlyWage) {
      const wage = parseFloat(hourlyWage);
      if (wage > 0) {
        const grossAnnual = wage * 40 * 52;
        setResults(calculateTakeHome(grossAnnual));
      }
    } else if (mode === 'annual' && annualSalary) {
      const salary = parseFloat(annualSalary);
      if (salary > 0) {
        setResults(calculateTakeHome(salary));
      }
    } else {
      setResults(null);
    }
  }, [mode, hourlyWage, annualSalary]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyDetailed = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center mb-4">
            <SafeIcon icon={FiCalculator} className="text-4xl text-teal-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Salary Calculator</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Calculate your take-home pay and understand your earnings potential. 
            Get accurate estimates based on 2025 federal and Maryland tax rates.
          </p>
        </motion.div>

        {/* Mode Toggle */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex items-center justify-center space-x-4">
            <span className={`font-medium ${mode === 'hourly' ? 'text-teal-600' : 'text-gray-500'}`}>
              Hourly to Annual
            </span>
            <button
              onClick={() => setMode(mode === 'hourly' ? 'annual' : 'hourly')}
              className="text-3xl text-teal-600 hover:text-teal-700 transition-colors"
            >
              <SafeIcon icon={mode === 'hourly' ? FiToggleLeft : FiToggleRight} />
            </button>
            <span className={`font-medium ${mode === 'annual' ? 'text-teal-600' : 'text-gray-500'}`}>
              Annual to Breakdown
            </span>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center mb-6">
              <SafeIcon icon={mode === 'hourly' ? FiClock : FiDollarSign} className="text-2xl text-teal-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">
                {mode === 'hourly' ? 'Enter Hourly Wage' : 'Enter Annual Salary'}
              </h2>
            </div>

            {mode === 'hourly' ? (
              <div>
                <label htmlFor="hourlyWage" className="block text-sm font-medium text-gray-700 mb-2">
                  Hourly Wage
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                  <input
                    type="number"
                    id="hourlyWage"
                    value={hourlyWage}
                    onChange={(e) => setHourlyWage(e.target.value)}
                    placeholder="25.00"
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-lg"
                    step="0.01"
                    min="0"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Based on 40 hours per week, 52 weeks per year
                </p>
              </div>
            ) : (
              <div>
                <label htmlFor="annualSalary" className="block text-sm font-medium text-gray-700 mb-2">
                  Annual Salary
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                  <input
                    type="number"
                    id="annualSalary"
                    value={annualSalary}
                    onChange={(e) => setAnnualSalary(e.target.value)}
                    placeholder="52000"
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-lg"
                    step="1000"
                    min="0"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Gross annual salary before taxes
                </p>
              </div>
            )}

            {/* Tax Info */}
            <div className="mt-6 p-4 bg-teal-50 rounded-lg">
              <div className="flex items-start">
                <SafeIcon icon={FiInfo} className="text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-teal-800">
                  <p className="font-medium mb-1">Tax Calculation Details:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• 2025 Federal tax brackets</li>
                    <li>• Maryland state tax (2% - 6.50%)</li>
                    <li>• Social Security (6.2%)</li>
                    <li>• Medicare (1.45%)</li>
                    <li>• Standard deduction: $15,000</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {mode === 'hourly' ? 'Annual Breakdown' : 'Salary Breakdown'}
            </h2>

            {results ? (
              <div className="space-y-4">
                {/* Gross Pay */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Gross Annual Salary</span>
                    <span className="text-xl font-bold text-gray-900">
                      {formatCurrency(results.grossAnnual)}
                    </span>
                  </div>
                  {mode === 'annual' && (
                    <div className="mt-2 text-sm text-gray-600">
                      Hourly equivalent: {formatCurrencyDetailed(results.hourlyEquivalent)}/hour
                    </div>
                  )}
                </div>

                {/* Tax Breakdown */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-800 border-b pb-2">Tax Deductions</h3>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Federal Income Tax</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(results.federalTax)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Maryland State Tax</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(results.stateTax)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Social Security (6.2%)</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(results.socialSecurity)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Medicare (1.45%)</span>
                    <span className="font-medium text-red-600">
                      -{formatCurrency(results.medicare)}
                    </span>
                  </div>
                  
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-medium text-gray-700">Total Taxes</span>
                    <span className="font-bold text-red-600">
                      -{formatCurrency(results.totalTax)}
                    </span>
                  </div>
                </div>

                {/* Net Pay */}
                <div className="bg-teal-50 rounded-lg p-4 border-2 border-teal-200">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-teal-800">Annual Take-Home</span>
                    <span className="text-2xl font-bold text-teal-600">
                      {formatCurrency(results.netAnnual)}
                    </span>
                  </div>
                  <div className="text-sm text-teal-700">
                    <div>Monthly: {formatCurrency(results.netAnnual / 12)}</div>
                    <div>Bi-weekly: {formatCurrency(results.netBiweekly)}</div>
                    <div>Weekly: {formatCurrency(results.netAnnual / 52)}</div>
                  </div>
                </div>

                {/* Effective Tax Rate */}
                <div className="text-center p-3 bg-gray-100 rounded-lg">
                  <span className="text-sm text-gray-600">Effective Tax Rate: </span>
                  <span className="font-bold text-gray-800">
                    {((results.totalTax / results.grossAnnual) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <SafeIcon icon={FiCalculator} className="text-6xl text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  Enter your {mode === 'hourly' ? 'hourly wage' : 'annual salary'} to see the breakdown
                </p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Disclaimer */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6"
        >
          <div className="flex items-start">
            <SafeIcon icon={FiInfo} className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-yellow-800">
              <h3 className="font-semibold mb-2">Important Disclaimer</h3>
              <p className="mb-2">
                This calculator provides estimates based on 2025 federal tax brackets and Maryland state tax rates. 
                Actual take-home pay may vary based on:
              </p>
              <ul className="list-disc ml-4 space-y-1">
                <li>Filing status (single, married, etc.)</li>
                <li>Additional deductions and credits</li>
                <li>Pre-tax benefits (health insurance, 401k, etc.)</li>
                <li>Local taxes and other withholdings</li>
                <li>Changes in tax laws</li>
              </ul>
              <p className="mt-2 font-medium">
                Consult a tax professional for personalized advice.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mt-8 text-gray-500 text-sm"
        >
          <p>Professional Salary Calculator • Built for Job Seekers • Updated for 2025 Tax Year</p>
        </motion.div>
      </div>
    </div>
  );
}

export default App;